import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import jwt_decode from 'jwt-decode';
// Components
import LoadingOverlay from 'components/common/LoadingOverlay';
import OrdersTabs from 'components/orders/OrdersTabs';
import OrdersFilterBar from 'components/orders/OrdersFilterBar';
import RankedOrdersList from 'components/orders/RankedOrdersList';
import DroppedOrdersTable from 'components/orders/DroppedOrdersTable';
import HofDashboardTable from 'components/orders/HofDashboardTable';
import MyTasksCounts from 'components/orders/MyTasksCounts';
// Helpers
import { formatDateForInput } from 'constants/Helpers';
import {
  buildOrdersParams,
  buildAssignedOrdersParams,
  buildDroppedOrdersParams,
  buildFloristsParams,
  defaultOrdersFilters,
  ORDERS_TABS,
  ALL_FLORISTS,
} from 'constants/OrdersFilters';
import { useOnScreen } from 'components/common/hooks/useOnScreen';
// Actions
import { setOpsNames } from 'store/orders/OrdersListActions';
// API
import OrdersListService from 'services/OrdersListService';
import FloristAssignmentService from 'services/FloristAssignmentService';
// Selectors
import {
  getDeliveryData,
  getOrdersSearchKey,
  getOrdersRefreshKey,
} from 'store/orders/OrdersListSelectors';
// Localstorage
import { getFromLocalStorage } from 'helpers/LocalStorage';

const REFRESH_INTERVAL = 5 * 60 * 1000;

const getCurrentFloristId = () => {
  try {
    return getFromLocalStorage('floristId') ?? jwt_decode(getFromLocalStorage('token'))?.id;
  } catch (err) {
    return null;
  }
};

const HFHomePage = () => {
  const dispatch = useDispatch();
  const listEndRef = useRef();
  const requestIdRef = useRef(0);
  const isAppendingRef = useRef(false);
  const onScreen = useOnScreen(listEndRef, '200px');
  // Typed in the header, kept in the store so the URL stays clean.
  const searchKey = useSelector((state) => getOrdersSearchKey({ state }));
  const storedDeliveryDate = useSelector((state) => getDeliveryData({ state }));
  // Bumped by the shift toggle in the header.
  const refreshKey = useSelector((state) => getOrdersRefreshKey({ state }));

  const [ordersList, setOrdersList] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [showLoader, setShowLoader] = useState(false);
  // A failed load must not read as "nothing to do" - see CHAR-11657.
  const [loadError, setLoadError] = useState(false);
  // HOF dashboard only - searches the florists list by name.
  const [hofSearch, setHofSearch] = useState('');
  // My Tasks only - the totals shown above the list.
  const [myTasksCounts, setMyTasksCounts] = useState(null);
  // Filters live in state, never in the URL - /home carries no query string.
  const [filters, setFilters] = useState(() => ({
    ...defaultOrdersFilters(),
    deliveryDate: formatDateForInput(storedDeliveryDate),
  }));

  // All four list tabs share the same filters and response shape. My Tasks and Overdue
  // Orders each read their own endpoint - the assigned one is already scoped to the
  // caller, and the overdue one carries its own rank sequence. Completed Orders is the
  // main endpoint with isDone flipped.
  const isOverdueTab = filters.tab === ORDERS_TABS.OVERDUE;
  const isMyTasksTab = filters.tab === ORDERS_TABS.MY_TASKS;
  const isCompletedTab = filters.tab === ORDERS_TABS.COMPLETED;
  // Dropped orders come from the assignment service and render as a table.
  const isDroppedTab = filters.tab === ORDERS_TABS.DROPPED;
  // The HOF dashboard lists florists rather than orders.
  const isHofTab = filters.tab === ORDERS_TABS.HOF;
  const floristId = isMyTasksTab ? +getCurrentFloristId() || ALL_FLORISTS : ALL_FLORISTS;
  // eslint-disable-next-line no-nested-ternary
  const emptyText = isOverdueTab
    ? 'No overdue orders'
    : isCompletedTab
    ? 'No completed orders'
    : 'No orders';

  const handleFiltersChange = (partialFilters) => {
    if (partialFilters.deliveryDate) {
      // Other pages read the picked date off the store.
      dispatch({
        type: 'SET_DELIVERY_DATE',
        payload: new Date(partialFilters.deliveryDate),
      });
    }
    setFilters((prev) => ({
      ...prev,
      ...partialFilters,
      ...(partialFilters.deliveryDate
        ? { deliveryDate: formatDateForInput(partialFilters.deliveryDate) }
        : {}),
    }));
  };

  const handleTabSelect = (tabKey) => {
    // Clear at once so the previous tab's cards can't sit under the new tab.
    setOrdersList([]);
    setPageCount(1);
    setLoadError(false);
    handleFiltersChange({ tab: tabKey });
  };

  const fetchOrders = useCallback(
    async (page, { append = false, silent = false } = {}) => {
      // Scroll loading is the only case where a second call in flight is wasteful.
      if (append && isAppendingRef.current) {
        return;
      }
      // Every call takes a ticket: switching tabs mid-flight must not let the older
      // response land on the newer tab's list.
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      if (append) {
        isAppendingRef.current = true;
      }
      if (!silent) {
        setShowLoader(true);
      }
      try {
        let res;
        if (isHofTab) {
          res = await FloristAssignmentService.getFlorists(
            buildFloristsParams({
              opsLevel: filters.opsLevel,
              opsId: filters.opsId,
              search: hofSearch,
            })
          );
        } else if (isDroppedTab) {
          // Only the OPS scope carries over to this list.
          res = await FloristAssignmentService.getDroppedOrders(
            buildDroppedOrdersParams({ opsLevel: filters.opsLevel, opsId: filters.opsId, page })
          );
        } else if (isMyTasksTab) {
          res = await OrdersListService.getAssignedOrdersList(
            buildAssignedOrdersParams({ ...filters, page, floristId })
          );
        } else {
          const params = buildOrdersParams({
            ...filters,
            page,
            searchKey,
            isDone: isCompletedTab,
          });
          if (isOverdueTab) {
            res = await OrdersListService.getOverdueOrdersList(params);
          } else if (isCompletedTab) {
            res = await OrdersListService.getCompletedOrders(params);
          } else {
            res = await OrdersListService.getordersListData(params);
          }
        }
        if (requestId !== requestIdRef.current) {
          return;
        }
        setLoadError(false);
        // Most endpoints answer with a paged envelope; the florists list is a bare array.
        const payload = res?.data;
        const data = (Array.isArray(payload) ? payload : payload?.data) ?? [];
        // That list is unpaged too - don't let the scroll loader ask for page 2.
        setPageCount(isHofTab ? 1 : payload?.pageCount ?? 1);
        setCurrentPage(page);
        setOrdersList((prev) => (append ? [...prev, ...data] : data));
        // Orders carry their OPS name; dropped rows only carry the id, so keep the
        // pairs around for them.
        dispatch(
          setOpsNames(
            Object.fromEntries(
              data
                .filter((row) => row?.opsId != null && row?.opsName)
                .map((row) => [row.opsId, row.opsName])
            )
          )
        );
      } catch (err) {
        if (!append && requestId === requestIdRef.current) {
          setOrdersList([]);
          setPageCount(1);
          setLoadError(true);
        }
      } finally {
        if (append) {
          isAppendingRef.current = false;
        }
        if (!silent && requestId === requestIdRef.current) {
          setShowLoader(false);
        }
      }
    },
    [
      dispatch,
      filters,
      searchKey,
      floristId,
      isOverdueTab,
      isMyTasksTab,
      isCompletedTab,
      isDroppedTab,
      isHofTab,
      hofSearch,
    ]
  );

  useEffect(() => {
    fetchOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchKey, hofSearch, refreshKey]);

  // Totals for My Tasks, scoped the same way as the list itself.
  useEffect(() => {
    if (!isMyTasksTab) {
      setMyTasksCounts(null);
      return;
    }
    let isCurrent = true;
    const fetchCounts = async () => {
      try {
        const res = await OrdersListService.getAssignedOrdersCount(
          buildAssignedOrdersParams({ ...filters, floristId })
        );
        if (isCurrent) {
          setMyTasksCounts(res?.data ?? null);
        }
      } catch (err) {
        if (isCurrent) {
          setMyTasksCounts(null);
        }
      }
    };
    fetchCounts();
    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyTasksTab, filters, floristId, refreshKey]);

  useEffect(() => {
    if (onScreen && !showLoader && currentPage < pageCount) {
      fetchOrders(currentPage + 1, { append: true, silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onScreen, currentPage, pageCount, showLoader]);

  // Ranks and cut-off timers age quickly - keep the list fresh without a visible reload.
  useEffect(() => {
    const intervalId = setInterval(() => fetchOrders(1, { silent: true }), REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [fetchOrders]);

  return (
    <div className="ranked-orders-page">
      {showLoader && <LoadingOverlay />}

      <OrdersFilterBar filters={filters} onFiltersChange={handleFiltersChange} />

      <OrdersTabs
        deliveryDate={filters.deliveryDate}
        activeTab={filters.tab}
        onTabSelect={handleTabSelect}
      />

      <div className="assign-to-me-note">
        <b>Assign to Me</b> lets any online florist pick up an unassigned or dropped order directly —
        operations don&apos;t have to wait on an HOF being available to reassign it.
      </div>

      {isMyTasksTab && <MyTasksCounts counts={myTasksCounts} />}

      {loadError ? (
        <div className="list-load-error">
          <p>Something went wrong. Please try again.</p>
          <button type="button" onClick={() => fetchOrders(1)}>
            Try again
          </button>
        </div>
      ) : isHofTab ? (
        <HofDashboardTable
          rows={ordersList}
          isLoading={showLoader}
          search={hofSearch}
          onSearchChange={setHofSearch}
        />
      ) : isDroppedTab ? (
        <DroppedOrdersTable
          rows={ordersList}
          isLoading={showLoader}
          onReassigned={() => fetchOrders(1)}
        />
      ) : (
        <RankedOrdersList
          ordersListData={ordersList}
          initialParams={
            isMyTasksTab
              ? buildAssignedOrdersParams({ ...filters, page: 1, floristId })
              : buildOrdersParams({ ...filters, page: 1, searchKey, isDone: isCompletedTab })
          }
          fetchMoreData={() => fetchOrders(1)}
          isLoading={showLoader}
          emptyText={emptyText}
          isMyTasks={isMyTasksTab}
        />
      )}
      <div ref={listEndRef} />
    </div>
  );
};

export default HFHomePage;
